import { useEffect, useRef, useState } from "react";

/**
 * Componente slot pubblicitario Google AdSense.
 *
 * Fin quando non viene fornito uno `slot` ID valido, NON renderizza alcun placeholder visuale
 * (niente box vuoto, niente scheletro) → il sito resta pulito al 100% nel periodo di review AdSense.
 *
 * Quando Luca riceve l'approvazione AdSense e crea un "ad unit" nel pannello, basta passare
 * lo slot ID (es. "1234567890") e il banner si attiverà automaticamente.
 *
 * Lazy-loaded via IntersectionObserver: lo script AdSense viene inizializzato SOLO quando
 * lo slot entra effettivamente nel viewport, per non rallentare il first paint.
 *
 * Props:
 * - slot (string): l'ID dell'ad unit AdSense (es. "1234567890"). Se vuoto/null, il componente non renderizza nulla.
 * - format (string): "auto" (default), "fluid", "rectangle", ecc. Vedi docs AdSense.
 * - layout (string, opzionale): per "in-article" passare "in-article".
 * - className (string, opzionale): classi Tailwind aggiuntive per il contenitore.
 * - testId (string, opzionale): data-testid per QA.
 */
export default function AdSlot({ slot, format = "auto", layout, className = "", testId = "ad-slot" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Lazy-load: osserva quando l'elemento entra nel viewport
  useEffect(() => {
    if (!slot) return;
    if (!ref.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
          }
        });
      },
      { rootMargin: "200px" }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [slot]);

  // Inizializza AdSense una volta visibile
  useEffect(() => {
    if (!visible || initialized || !slot) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      setInitialized(true);
    } catch (err) {
      // AdSense non caricato (es. ad-blocker): fail silently
      // eslint-disable-next-line no-console
      console.debug("[AdSlot] adsbygoogle push failed", err);
    }
  }, [visible, initialized, slot]);

  // Se non c'è slot ID → non renderizziamo NIENTE (sito pulito prima dell'approvazione)
  if (!slot) return null;

  return (
    <div ref={ref} className={`ad-slot-wrapper my-6 ${className}`} data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-[#8A9691] font-ui mb-1 text-center">
        Pubblicità
      </p>
      <ins
        className="adsbygoogle"
        style={{ display: "block", textAlign: "center" }}
        data-ad-client="ca-pub-2157861340871535"
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive="true"
        {...(layout ? { "data-ad-layout": layout } : {})}
      />
    </div>
  );
}
